import './review-sections.css'

interface Vital {
  metric: string
  current_value: string
  cycle_start_value: string
  ivp_norm_range: string
  status: string
  movement: string
  context: string
}

interface HealthCheckData {
  overall_health: string
  vitals: Vital[]
}

interface Props {
  data: HealthCheckData
}

function getHealthColor(health: string): string {
  switch (health) {
    case 'strong': return '#2e7d32'
    case 'moderate': return '#e65100'
    case 'weak': return '#c62828'
    case 'critical': return '#b71c1c'
    default: return '#666'
  }
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'healthy': return 'bsc-financial'
    case 'watch': return 'bsc-process'
    case 'below_norm': return 'urgency-high'
    case 'critical': return 'urgency-high'
    default: return ''
  }
}

export default function HealthCheckSection({ data }: Props) {
  const healthColor = getHealthColor(data.overall_health)

  return (
    <div>
      <p className="section-hint">
        How your company's key metrics compare to industry benchmarks, and how they moved during this cycle.
      </p>

      <div style={{ marginBottom: 16, fontSize: 14, fontWeight: 600 }}>
        Overall Health:{' '}
        <span style={{ color: healthColor, textTransform: 'capitalize' }}>
          {data.overall_health}
        </span>
      </div>

      {(data.vitals || []).map((vital, i) => {
        const hasMovement = vital.movement && vital.movement !== 'N/A' && vital.movement !== 'n/a'
        const isPositive = hasMovement && vital.movement.startsWith('+')
        return (
          <div key={i} className="review-card">
            <div className="review-card-header">
              <span className="review-card-title">{vital.metric}</span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {hasMovement && (
                  <span className={`movement-badge ${isPositive ? 'movement-positive' : 'movement-negative'}`}>
                    {vital.movement}
                  </span>
                )}
                <span className={`bsc-badge ${getStatusClass(vital.status)}`}>
                  {vital.status.replace('_', ' ')}
                </span>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 12, marginBottom: 8 }}>
              <div><strong>Start:</strong> {vital.cycle_start_value}</div>
              <div><strong>Current:</strong> {vital.current_value}</div>
              <div><strong>IVP Norm:</strong> {vital.ivp_norm_range}</div>
            </div>
            <p className="review-card-body">{vital.context}</p>
          </div>
        )
      })}

      {(!data.vitals || data.vitals.length === 0) && (
        <p style={{ fontSize: 13, color: '#666', fontStyle: 'italic' }}>
          No vitals data available for this cycle.
        </p>
      )}
    </div>
  )
}
